"use client";

import { useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useState } from "react";
import type { BillingOverviewQuery } from "@/lib/billing/useBillingOverview";
import { useGraphqlMutation } from "@/lib/graphql/useGraphql";

const BILLING_OVERVIEW_KEY = ["billing-overview"];

// The charge is synchronous, but the credit balance is applied asynchronously by
// the Stripe `invoice.paid` webhook, which typically lands a second or two after
// this mutation returns. Poll the billing overview until the balance reflects the
// new credit (or we exhaust the attempts) so the UI doesn't show a stale balance.
const TOP_UP_BALANCE_POLL_ATTEMPTS = 8;
const TOP_UP_BALANCE_POLL_INTERVAL_MS = 1500;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type CreditTopUpResult = {
  invoice_id: string;
  status: string;
  amount_cents: number;
};

type CreateCreditTopUpData = {
  createCreditTopUp: CreditTopUpResult;
};

type CreateCreditTopUpVariables = {
  // Sent as a string to the BigInt scalar — a $5,000 top-up is 5,000,000,000
  // micro-USD, which overflows GraphQL's 32-bit Int.
  amount_micro: string;
};

const createCreditTopUpDocument = gql`
  mutation CreateCreditTopUp($amount_micro: BigInt!) {
    createCreditTopUp(amount_micro: $amount_micro) {
      invoice_id
      status
      amount_cents
    }
  }
`;

export const useCreditTopUp = () => {
  const queryClient = useQueryClient();

  // True from the moment a charge succeeds until the webhook-applied credit lands
  // (or polling gives up). Consumers use this to show a loading state for the
  // balance, since the charge completes before the balance actually updates.
  const [isAwaitingCredit, setIsAwaitingCredit] = useState(false);

  // credit_balance_micro is a string (to avoid GraphQL Int32 overflow); coerce to a
  // number so the poll comparison below is numeric rather than lexicographic.
  const readBalanceMicro = (): number | null => {
    const raw =
      queryClient.getQueryData<BillingOverviewQuery>(BILLING_OVERVIEW_KEY)?.billing_overview?.credit_balance_micro;

    return raw == null ? null : Number(raw);
  };

  // Refetch immediately, then keep polling until the webhook-applied credit shows
  // up (or attempts are exhausted). If we can't read the prior balance (cache
  // miss), a single refetch is the best we can do.
  const pollUntilCredited = async () => {
    // Treat a missing cache entry as 0 so we keep polling until the webhook
    // increases the balance, rather than bailing out on the first null read.
    const balanceBefore = readBalanceMicro() ?? 0;
    setIsAwaitingCredit(true);

    try {
      for (let attempt = 0; attempt < TOP_UP_BALANCE_POLL_ATTEMPTS; attempt++) {
        await queryClient.invalidateQueries({ queryKey: BILLING_OVERVIEW_KEY });

        const balanceAfter = readBalanceMicro();
        if (balanceAfter !== null && balanceAfter > balanceBefore) {
          return;
        }

        await sleep(TOP_UP_BALANCE_POLL_INTERVAL_MS);
      }
    } finally {
      setIsAwaitingCredit(false);
    }
  };

  const mutation = useGraphqlMutation<CreateCreditTopUpData, CreateCreditTopUpVariables>({
    mutationKey: ["create-credit-top-up"],
    document: createCreditTopUpDocument,
    // Fire-and-forget: returning this promise would make React Query await the full
    // poll window before running the caller's onSuccess (which closes the dialog),
    // leaving the dialog stuck on "Processing…". The poll updates the shared query
    // cache in the background, so the visible balance refreshes on its own.
    onSuccess: () => {
      void pollUntilCredited();
    },
  });

  return { ...mutation, isAwaitingCredit };
};
